import type { FrameArtworkProfile } from '../types/doorConfiguration';
import type { RectMm } from '../layout/doorLayout';

export interface FrameProfileViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FrameProfileAssetMeta {
  profile: FrameArtworkProfile;
  publicPath: string;
  /** Bump whenever canonical SVG contents change so fetched markup cannot remain stale. */
  contentVersion: string;
  /** Base colour represented by the asset's authored `#frame-colour-palette`. */
  authoredBaseHex: string;
  viewBox: FrameProfileViewBox;
  /** Source-SVG user-unit regions to clip. Not millimetres. Not product cill/threshold bounds. */
  clipRects: {
    slabPlaceholder: RectMm;
    /** Embedded threshold/cill artwork to hide; layout still owns real threshold/cill. */
    thresholdBand: RectMm;
  };
}

const CROPPED_VIEW_BOX: FrameProfileViewBox = {
  x: 0,
  y: 0,
  width: 621.29999,
  height: 1241.3,
};

/** Canonical production artwork. Combined/reference assets must never appear here. */
export const FRAME_PROFILE_CANONICAL_PATHS: Record<FrameArtworkProfile, string> = {
  chamfered: '/images/door-designer/frame-profiles/chamfered.svg',
  sculptured: '/images/door-designer/frame-profiles/sculptured.svg',
  timberbutt: '/images/door-designer/frame-profiles/timberbutt.svg',
};

export const FRAME_PROFILE_ASSETS: Record<FrameArtworkProfile, FrameProfileAssetMeta> = {
  chamfered: {
    profile: 'chamfered',
    publicPath: FRAME_PROFILE_CANONICAL_PATHS.chamfered,
    contentVersion: '2026-08-15.2',
    authoredBaseHex: '#EEEEEB',
    viewBox: CROPPED_VIEW_BOX,
    clipRects: {
      slabPlaceholder: { x: 55.65, y: 55.65, width: 510, height: 1117 },
      // Includes the gasket that sits just above the 65-unit band (inner y=1252).
      thresholdBand: { x: 0, y: 1172, width: 621.29999, height: 69.3 },
    },
  },
  sculptured: {
    profile: 'sculptured',
    publicPath: FRAME_PROFILE_CANONICAL_PATHS.sculptured,
    contentVersion: '2026-08-14.1',
    authoredBaseHex: '#BFD4D0',
    viewBox: CROPPED_VIEW_BOX,
    clipRects: {
      slabPlaceholder: { x: 55.65, y: 57.15, width: 510, height: 1117 },
      thresholdBand: { x: 0, y: 1172, width: 621.29999, height: 69.3 },
    },
  },
  timberbutt: {
    profile: 'timberbutt',
    publicPath: FRAME_PROFILE_CANONICAL_PATHS.timberbutt,
    contentVersion: '2026-08-19.1',
    authoredBaseHex: '#BFD4D0',
    viewBox: CROPPED_VIEW_BOX,
    clipRects: {
      slabPlaceholder: { x: 55.65, y: 57.15, width: 510, height: 1117 },
      thresholdBand: { x: 0, y: 1172, width: 621.29999, height: 69.3 },
    },
  },
};

export function getFrameProfileAsset(profile: FrameArtworkProfile): FrameProfileAssetMeta {
  return FRAME_PROFILE_ASSETS[profile];
}

export function shouldOverrideFrameProfilePalette(
  asset: FrameProfileAssetMeta,
  selectedBaseHex: string
): boolean {
  return selectedBaseHex.toUpperCase() !== asset.authoredBaseHex.toUpperCase();
}
